import { combineLatest, defer, Observable, of } from 'rxjs'
import { map, startWith, switchMap } from 'rxjs/operators'
import type { TositeKortinTiedot } from './lisaa-tositteita.component'

export interface TositteenTallennusesto {
  puutteellinenLomake: boolean
  kasittelyvirhe: boolean
  latausKesken: boolean
  latausEpaonnistui: boolean
  lukuKesken: boolean
}

export interface TallennuksenEstojenYhteenveto {
  korjattavienMaara: number
  odottaviaLatauksia: boolean
  odottaviaLukuja: boolean
}

export const onkoTiedostovirhe = (esto: TositteenTallennusesto) => esto.kasittelyvirhe || esto.latausEpaonnistui

export const onkoOdottava = (esto: TositteenTallennusesto) => esto.latausKesken || esto.lukuKesken

// Kentät ovat tyhjiä, kunnes lähetys ja tietojen luku valmistuvat, joten puuttuvia tietoja ei pyydetä korjaamaan sitä ennen.
export const onkoKorjattava = (esto: TositteenTallennusesto) => onkoTiedostovirhe(esto) || (esto.puutteellinenLomake && !onkoOdottava(esto))

export const onkoTallennusestoja = (esto: TositteenTallennusesto) => esto.puutteellinenLomake || onkoTiedostovirhe(esto) || esto.latausKesken

export const annaTositteenTallennusestoObservable = (tositteenTiedot: TositeKortinTiedot): Observable<TositteenTallennusesto> => {
  const lomake = tositteenTiedot.formGroup
  const puutteellinenLomakeObservable = defer(() => lomake.statusChanges.pipe(startWith(lomake.status))).pipe(map(status => status !== 'VALID'))
  const tiedostoestotObservable = tositteenTiedot.sivutSubject.pipe(
    switchMap(sivut => {
      if (sivut.length === 0) {
        return of({ kasittelyvirhe: false, latausKesken: false, latausEpaonnistui: false })
      }
      return combineLatest(sivut.map(sivu => sivu.uploadData.statusObservable)).pipe(
        map(statukset => ({
          kasittelyvirhe: sivut.some(sivu => sivu.purkuvirhe),
          latausKesken: statukset.some(status => !status.done),
          latausEpaonnistui: statukset.some(status => status.done && !!status.error),
        })),
      )
    }),
  )
  const lukuKeskenObservable = tositteenTiedot.lukuTilaSubject.pipe(map(tila => tila === 'luetaan' || tila === 'taydennetaan'))
  return combineLatest([puutteellinenLomakeObservable, tiedostoestotObservable, lukuKeskenObservable]).pipe(
    map(([puutteellinenLomake, tiedostoestot, lukuKesken]) => ({ puutteellinenLomake, ...tiedostoestot, lukuKesken })),
  )
}

export const muodostaTallennuksenEstojenYhteenveto = (estot: TositteenTallennusesto[]): TallennuksenEstojenYhteenveto => ({
  korjattavienMaara: estot.filter(onkoKorjattava).length,
  odottaviaLatauksia: estot.some(esto => esto.latausKesken),
  odottaviaLukuja: estot.some(esto => esto.lukuKesken && esto.puutteellinenLomake),
})
